import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import { useAuth } from "./contexts/AuthContext";
import Login from "./pages/login";
import CustomerDatabase from './pages/customer-database';
import TechnicianAssignment from './pages/technician-assignment';
import Dashboard from './pages/dashboard';
import SupportRequestManagement from './pages/support-request-management';
import PersonnelManagement from './pages/personnel-management';
import TicketManagement from './pages/ticket-management';
import Products from './pages/products';
import DataManagement from './pages/data-management';
import InventoryTrackingDashboard from './pages/inventory-tracking-dashboard';
import InventoryAnalyticsDashboard from './pages/inventory-analytics-dashboard';
import QuoteManagement from './pages/quote-management';
import InvoiceManagementCenter from './pages/invoice-management-center';
import CustomerPriceListManagement from './pages/customer-price-list-management';
import Reports from './pages/reports';
import MyDashboard from './pages/my-dashboard';
import CustomerQuotePortal from './pages/customer-quote-portal';
import MobileTechnicianApp from './pages/mobile-technician-app';
import OrganizationSettings from './pages/organization-settings';
import SubscriptionBilling from './pages/subscription-billing';
import CompanyUsers from './pages/company-users';
import OrganizationOnboarding from './pages/organization-onboarding';
import AdminOperatorDashboard from './pages/admin-operator-dashboard';
import EmailTemplates from './pages/email-templates';
import OrganizationDashboard from './pages/organization-dashboard';
import AuditActivityLog from './pages/audit-activity-log';
import TaxRateSettings from 'pages/tax-rate-settings';
import CustomerPaymentPortal from './pages/customer-payment-portal';
import InvoiceReconciliationDashboard from './pages/invoice-reconciliation-dashboard';
import PaymentRemindersDunning from './pages/payment-reminders-dunning';
import RecurringBilling from './pages/recurring-billing';
import AccountingSync from './pages/accounting-sync';
import CustomerTicketPortal from './pages/customer-ticket-portal';
import InvoiceTemplateCustomization from './pages/invoice-template-customization';
import FailedPaymentRecovery from './pages/failed-payment-recovery';
import RoleGuard from './components/RoleGuard';
import SuperAdminOrganizations from './pages/superadmin-organizations';
import RolePermissionsSettings from './pages/role-permissions-settings';
import PermissionManagement from './pages/permission-management';
import SecurityAuditLog from './pages/security-audit-log';
import ApiKeyManagement from './pages/api-key-management';
import SystemHealthDashboard from './pages/system-health-dashboard';
import DataExportBackup from './pages/data-export-backup';
import DocumentTemplatesManager from './pages/document-templates-manager';
import WorkflowAutomationCenter from './pages/workflow-automation-center';
import CustomerApprovalPortal from './pages/customer-approval-portal';
import ApprovalDashboard from './pages/approval-dashboard';
import ApprovalAnalyticsReports from './pages/approval-analytics-reports';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <RouterRoutes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/customer-database" element={<ProtectedRoute><CustomerDatabase /></ProtectedRoute>} />
        <Route path="/technician-assignment" element={<ProtectedRoute><TechnicianAssignment /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/support-request-management" element={<ProtectedRoute><SupportRequestManagement /></ProtectedRoute>} />
        <Route path="/personnel-management" element={<ProtectedRoute><PersonnelManagement /></ProtectedRoute>} />
        <Route path="/ticket-management" element={<ProtectedRoute><TicketManagement /></ProtectedRoute>} />
        <Route path="/products" element={<ProtectedRoute><Products /></ProtectedRoute>} />
        <Route path="/data-management" element={<ProtectedRoute><DataManagement /></ProtectedRoute>} />
        <Route path="/inventory-tracking-dashboard" element={<ProtectedRoute><InventoryTrackingDashboard /></ProtectedRoute>} />
        <Route path="/inventory-analytics-dashboard" element={<ProtectedRoute><InventoryAnalyticsDashboard /></ProtectedRoute>} />
        <Route path="/quote-management" element={<ProtectedRoute><QuoteManagement /></ProtectedRoute>} />
        <Route path="/invoice-management-center" element={<ProtectedRoute><InvoiceManagementCenter /></ProtectedRoute>} />
        <Route path="/customer-price-list-management" element={<ProtectedRoute><CustomerPriceListManagement /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
        <Route path="/my-dashboard" element={<ProtectedRoute><MyDashboard /></ProtectedRoute>} />
        <Route path="/customer-quote-portal" element={<ProtectedRoute><CustomerQuotePortal /></ProtectedRoute>} />
        <Route path="/mobile-technician-app" element={<ProtectedRoute><MobileTechnicianApp /></ProtectedRoute>} />
        <Route path="/organization-settings" element={<ProtectedRoute><OrganizationSettings /></ProtectedRoute>} />
        <Route path="/subscription-billing" element={<ProtectedRoute><SubscriptionBilling /></ProtectedRoute>} />
        <Route path="/company-users" element={<ProtectedRoute><CompanyUsers /></ProtectedRoute>} />
        <Route path="/organization-onboarding" element={<ProtectedRoute><OrganizationOnboarding /></ProtectedRoute>} />
        <Route path="/admin-operator-dashboard" element={<ProtectedRoute><AdminOperatorDashboard /></ProtectedRoute>} />
        <Route path="/email-templates" element={<ProtectedRoute><EmailTemplates /></ProtectedRoute>} />
        <Route path="/organization-dashboard" element={<ProtectedRoute><OrganizationDashboard /></ProtectedRoute>} />
        <Route path="/audit-activity-log" element={<ProtectedRoute><AuditActivityLog /></ProtectedRoute>} />
        <Route path="/tax-rate-settings" element={<ProtectedRoute><TaxRateSettings /></ProtectedRoute>} />
        <Route path="/customer-payment-portal" element={<CustomerPaymentPortal />} />
        <Route path="/customer-ticket-portal" element={<CustomerTicketPortal />} />
        <Route path="/customer-approval-portal" element={<CustomerApprovalPortal />} />
        <Route path="/invoice-template-customization" element={<ProtectedRoute><InvoiceTemplateCustomization /></ProtectedRoute>} />
        <Route path="/failed-payment-recovery" element={<ProtectedRoute><FailedPaymentRecovery /></ProtectedRoute>} />
        <Route path="/invoice-reconciliation-dashboard" element={<ProtectedRoute><InvoiceReconciliationDashboard /></ProtectedRoute>} />
        <Route path="/payment-reminders-dunning" element={<ProtectedRoute><PaymentRemindersDunning /></ProtectedRoute>} />
        <Route path="/recurring-billing" element={<ProtectedRoute><RecurringBilling /></ProtectedRoute>} />
        <Route path="/accounting-sync" element={<ProtectedRoute><AccountingSync /></ProtectedRoute>} />
        <Route
          path="/superadmin-organizations"
          element={
            <ProtectedRoute>
              <RoleGuard requireSuperAdmin>
                <SuperAdminOrganizations />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/role-permissions-settings"
          element={
            <ProtectedRoute>
              <RoleGuard requireOrgAdmin>
                <RolePermissionsSettings />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route path="/permission-management" element={<PermissionManagement />} />
        <Route path="/security-audit-log" element={<ProtectedRoute><SecurityAuditLog /></ProtectedRoute>} />
        <Route path="/api-key-management" element={<ProtectedRoute><ApiKeyManagement /></ProtectedRoute>} />
        <Route path="/system-health-dashboard" element={<ProtectedRoute><SystemHealthDashboard /></ProtectedRoute>} />
        <Route path="/data-export-backup" element={<ProtectedRoute><DataExportBackup /></ProtectedRoute>} />
        <Route path="/document-templates-manager" element={<ProtectedRoute><DocumentTemplatesManager /></ProtectedRoute>} />
        <Route path="/workflow-automation-center" element={<ProtectedRoute><WorkflowAutomationCenter /></ProtectedRoute>} />
        <Route path="/approval-dashboard" element={<ProtectedRoute><ApprovalDashboard /></ProtectedRoute>} />
        <Route path="/approval-analytics-reports" element={<ProtectedRoute><ApprovalAnalyticsReports /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
